import { AttributeReadModel } from "@Alternatives/Attributes/Domain/readmodels";
import { EmailSnippetReadModel, NotificationReadModel } from "@Alternatives/Notifications/Domain/readmodels";
import { AlternativeStatusReadModel } from "@Alternatives/Statuses/Domain/readmodels";
import { EditorState, generateName } from "@common/Common";
import { ValidationError } from "@common/types";

export class NotificationEditorViewModel
{
    state: EditorState = EditorState.Closed;
    statuses: AlternativeStatusReadModel[] = [];
    attributes: AttributeReadModel[] = [];
    emailSnippets: EmailSnippetReadModel[] = [];

    id: number | null = null;
    enabled: boolean | null = null;
    name: string | null = null;
    description: string | null = null;
    statusId: number | null = null;
    prevStatusId: number | null = null;
    emailSubject: string | null = null;
    emailReplyToEmail: string | null = null;
    emailRecipientAttributeId: number | null = null;
    emailAdHocRecipients: string | null = null;
    emailMessage: string | null = null;

    error: ValidationError | null = null;

    get opened(): boolean { return this.state !== EditorState.Closed; }

    showCreator(userEmail: string, alternativeLabel: string, defaultStatusId: number)
    {
        this.id = null;
        this.enabled = true;
        this.name = generateName("Notification");
        this.description = null;
        this.statusId = defaultStatusId;
        this.prevStatusId = null;

        this.emailSubject = null;
        this.emailReplyToEmail = userEmail;
        this.emailRecipientAttributeId = null;
        this.emailAdHocRecipients = null;
        this.emailMessage = alternativeLabel + " '{Name}' changed status from '{OldStatus}' to '{NewStatus}'.";

        this.state = EditorState.Creator;
    }

    showEditor(n: NotificationReadModel)
    {
        this.id = n.Id;
        this.enabled = n.Enabled;
        this.name = n.Name;
        this.description = n.Description;
        this.statusId = n.AlternativeStatusId;
        this.prevStatusId = n.PreviousAlternativeStatusId;

        this.emailSubject = n.Subject;
        this.emailReplyToEmail = n.ReplyToEmail;
        this.emailRecipientAttributeId = n.RecipientAttributeId;
        this.emailAdHocRecipients = n.AdHocRecipients;
        this.emailMessage = n.Message;

        this.state = EditorState.Editor;
    }

    setData(statuses: AlternativeStatusReadModel[], attributes: AttributeReadModel[], emailSnippets: EmailSnippetReadModel[])
    {
        this.statuses = statuses;
        this.attributes = attributes;
        this.emailSnippets = emailSnippets;
    }

    close()
    {
        this.state = EditorState.Closed;

        this.id = null;
        this.enabled = null;
        this.name = null;
        this.description = null;
        this.statusId = null;
        this.prevStatusId = null;
        this.emailSubject = null;
        this.emailReplyToEmail = null;
        this.emailRecipientAttributeId = null;
        this.emailAdHocRecipients = null;
        this.emailMessage = null;
    }

    displayValidationErrors(errors: ValidationError[])
    {
        this.error = errors[0];
    }

    clearValidationErrors()
    {
        this.error = null;
    }
}

export class CreateNotificationEventPayload
{
    name: string;
    description: string | null;
    enabled: boolean;
    statusId: number;
    prevStatusId: number | null;
    emailSubject: string;
    emailMessage: string;
    emailRecipientAttributeId: number | null;
    emailAdHocRecipients: string | null;
    emailReplyToEmail: string;

    constructor(name: string, description: string | null, enabled: boolean, statusId: number, prevStatusId: number | null,
        emailSubject: string, emailMessage: string, emailRecipientAttributeId: number | null, emailAdHocRecipients: string | null, emailReplyToEmail: string)
    {
        this.name = name;
        this.description = description;
        this.enabled = enabled;
        this.statusId = statusId;
        this.prevStatusId = prevStatusId;
        this.emailSubject = emailSubject;
        this.emailMessage = emailMessage;
        this.emailRecipientAttributeId = emailRecipientAttributeId;
        this.emailAdHocRecipients = emailAdHocRecipients;
        this.emailReplyToEmail = emailReplyToEmail;
    }
}

export class UpdateNotificationEventPayload
{
    id: number;
    name: string;
    description: string | null;
    enabled: boolean;
    statusId: number;
    prevStatusId: number | null;
    emailSubject: string;
    emailMessage: string;
    emailRecipientAttributeId: number | null;
    emailAdHocRecipients: string | null;
    emailReplyToEmail: string;

    constructor(id: number, name: string, description: string | null, enabled: boolean, statusId: number, prevStatusId: number | null,
        emailSubject: string, emailMessage: string, emailRecipientAttributeId: number | null, emailAdHocRecipients: string | null, emailReplyToEmail: string)
    {
        this.id = id;
        this.name = name;
        this.description = description;
        this.enabled = enabled;
        this.statusId = statusId;
        this.prevStatusId = prevStatusId;
        this.emailSubject = emailSubject;
        this.emailMessage = emailMessage;
        this.emailRecipientAttributeId = emailRecipientAttributeId;
        this.emailAdHocRecipients = emailAdHocRecipients;
        this.emailReplyToEmail = emailReplyToEmail;
    }
}